import { clipboard } from "@neutralinojs/lib";
import { ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useOpenUrl } from "../../hooks/useOpenUrl";

interface DesktopAuthProps {
  onAuthenticate: (code: string) => Promise<void>;
}

export function DesktopAuth({ onAuthenticate }: Readonly<DesktopAuthProps>) {
  const [code, setCode] = useState(["", "", "", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const openUrl = useOpenUrl();
  const isDesktop = useIsDesktop();

  // Get dashboard URL from environment
  const getDashboardUrl = () => {
    const dashUrl = import.meta.env.VITE_DASH_URL;
    if (!dashUrl) {
      throw new Error(
        "VITE_DASH_URL environment variable is required for desktop authentication",
      );
    }
    return dashUrl;
  };

  // Focus the first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    // Handle single character input (case insensitive, display uppercase)
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (sanitized.length <= 1) {
      const newCode = [...code];
      newCode[index] = sanitized;
      setCode(newCode);

      // Auto-focus next input
      if (sanitized && index < 7) {
        inputRefs.current[index + 1]?.focus();
      }

      // Clear error when user starts typing
      if (error) setError(null);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    e.preventDefault();
    let pastedText = "";
    if (isDesktop) {
      try {
        pastedText = await clipboard.readText();
      } catch (err) {
        console.error("Failed to read from Neutralino clipboard:", err);
        setError("Could not paste from clipboard.");
        return;
      }
    } else {
      pastedText = e.clipboardData.getData("text");
    }
    // Handle paste of full code (case insensitive)
    pastedText = pastedText
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8);

    const newCode = Array(8).fill("");
    for (let i = 0; i < pastedText.length; i++) {
      newCode[i] = pastedText[i];
    }
    setCode(newCode);

    // Focus the next empty input or last input
    const nextEmptyIndex = newCode.findIndex((c) => c === "");
    const focusIndex = nextEmptyIndex === -1 ? 7 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();

    // Clear error when user pastes
    if (error) setError(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter" && code.join("").length === 8 && !loading) {
      // Submit on Enter if code is complete
      handleAuthenticate();
    }
  };

  const handleAuthenticate = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 8) {
      setError("Please enter a valid 8-character code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAuthenticate(fullCode);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? (err.message ?? "Authentication failed")
          : "Authentication failed";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    console.log("🔘 Get code button clicked!");
    try {
      const dashboardUrl = getDashboardUrl();
      // Add a query parameter to indicate we want to go to link-desktop after sign-in
      const settingsUrl = `${dashboardUrl}/link-desktop?from=desktop`;
      console.log("Opening dashboard URL:", settingsUrl);
      await openUrl(settingsUrl);
    } catch (error) {
      console.error("❌ Error in handleOpenDashboard:", error);
      setError(
        error instanceof Error ? error.message : "Dashboard URL not configured",
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Desktop Authentication</CardTitle>
          <CardDescription>
            Enter the 8-character code from your dashboard to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Individual character inputs with paste support */}
          <div className="flex justify-center gap-1 sm:gap-2">
            {code.map((char, index) => (
              <input
                key={`code-input-position-${index}`}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                value={char}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-center text-sm sm:text-lg md:text-xl font-mono font-bold border-2 rounded-lg focus:border-primary focus:outline-none bg-background transition-colors"
                maxLength={1}
                placeholder="·"
                disabled={loading}
                data-custom-paste="true"
                data-testid={`code-input-${index}`}
              />
            ))}
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Type each character individually or paste the full code
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleAuthenticate}
            disabled={loading || code.join("").length !== 8}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Authenticating...
              </>
            ) : (
              "Authenticate"
            )}
          </Button>

          <div className="text-center space-y-3">
            <Button
              onClick={handleOpenDashboard}
              variant="outline"
              className="w-full"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Get code
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
